import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type { TextChain } from '@/api/types/stowed-element'
import { useAddDictionaryWord } from '@/api/hooks/use-add-dictionary-word'
import { useClearDictionary } from '@/api/hooks/use-clear-dictionary'
import { useDictionaryQuery } from '@/api/hooks/use-dictionary'
import { useDictionaryWordSync } from '@/api/hooks/use-dictionary-word-sync'
import { useRemoveDictionaryWord } from '@/api/hooks/use-remove-dictionary-word'
import {
  type SpellChecker,
  type SpellText,
  buildSpellIssues,
  createSpellScanner,
  createSuggester,
  loadSpellChecker,
} from '@/lib/spell-check/spell-engine'
import {
  type SpellCheckDictionaries,
  type SpellCheckState,
  type SpellIssue,
  type SpellUnderlineRange,
  applyResolutions,
  forgetWrittenChanges,
  newlyChangedOccurrences,
  reopenOccurrences,
  reopenRemovedWords,
  trackResolutionsThroughEdits,
  underlineRangesByShape,
} from '@/lib/spell-check/spell-check-state'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { setSpellActiveWord } from '@/components/app/shapes/shape-spell-active-word'
import { setSpellPeek } from '@/components/app/shapes/shape-spell-peek'
import {
  clearSpellUnderlines,
  setSpellUnderlines,
} from '@/components/app/shapes/shape-spell-underlines'
import {
  getShapeTextFramesRevision,
  subscribeToShapeTextFrames,
} from '@/components/app/shapes/shape-text-frame-registry'
import { logger } from '@/utils/logger'
import { collectShapeTexts } from '../handlers/collect-shape-texts'
import { notifyDictionaryUpdateFailed } from '../handlers/notify-dictionary-change-failed'
import { changeReplacements } from '../helpers/change-replacements'
import { rosterWords } from '../helpers/roster-words'
import type { DictionaryStatus } from '../types/dictionary-status'
import type { SpellCheckBinding } from '../types/spell-check-binding'
import type { SpellCheckStatus } from '../types/spell-check-status'
import type { SpellFocusWord } from '../types/spell-focus-word'

interface SpellCheckSettings {
  showUnderlines: boolean
  dictionaries: SpellCheckDictionaries
}

const INITIAL_SETTINGS: SpellCheckSettings = {
  showUnderlines: true,
  dictionaries: { custom: true, studentList: true },
}

// Case Sensitive starts ON for each spread.
const DEFAULT_CASE_SENSITIVE = true

const NO_UNDERLINES = new Map<string, readonly SpellUnderlineRange[]>()
const NO_WORDS: string[] = []
const NO_SUGGESTIONS = (): string[] => []
const NEVER_NOTIFIES = () => () => {}

interface UseSpellCheckOptions {
  /** The Spell Check tool is the active tool; nothing loads or runs until it is. */
  isActive: boolean
  shapeManagerRef?: RefObject<ShapeManagerRef | null>
  spreadId?: string
  /** Where the right page begins, so frames are read left page first; `null` for one page. */
  spineX?: number | null
  /** Bumped by the editor when text styles or chains change (`LeftPanelProps.textPanelRevision`). */
  textPanelRevision?: number
  /** The spread's text-link chains, so a word cut between two linked frames is checked whole. */
  textChains?: TextChain[]
}

/**
 * Spread-level spell check for the left panel. When the panel opens it lazy-loads the en_US
 * wordlist and fetches the school dictionary, then checks every text frame on the spread. The
 * scan is derived during render from the current text, so edits to the spread show up on the
 * next render; only the user's own choices (Ignore, Change, toggles) are kept as state.
 */
export function useSpellCheck({
  isActive,
  shapeManagerRef,
  spreadId,
  spineX = null,
  textPanelRevision = 0,
  textChains,
}: UseSpellCheckOptions): SpellCheckBinding {
  const dictionary = useDictionaryQuery(isActive)
  const syncWords = useDictionaryWordSync(notifyDictionaryUpdateFailed)
  const addWord = useAddDictionaryWord()
  const removeWord = useRemoveDictionaryWord()
  const clearWords = useClearDictionary()
  const [checker, setChecker] = useState<SpellChecker | null>(null)
  const [checkerFailed, setCheckerFailed] = useState(false)
  const [settings, setSettings] = useState(INITIAL_SETTINGS)
  // Case Sensitive is remembered per spread while the Page Editor stays open: switching spreads and
  // back keeps each one's choice, and reopening the editor starts every spread at ON (ED-5). It is
  // this user's setting only, so nothing is saved.
  const [caseSensitiveBySpread, setCaseSensitiveBySpread] = useState<Record<string, boolean>>({})
  const spreadKey = spreadId ?? ''
  const caseSensitive = caseSensitiveBySpread[spreadKey] ?? DEFAULT_CASE_SENSITIVE
  const [resolved, setResolved] = useState<readonly SpellIssue[]>([])
  // The text `resolved` was read from, so each choice can be followed to where its word is now
  // when the text is edited, even while the panel is closed (SC-14).
  const [resolvedTexts, setResolvedTexts] = useState<readonly SpellText[] | null>(null)

  // A failed dictionary fetch isn't retried by the query on its own, so each reopen of the panel
  // is the retry the error message promises. The ref keeps `isError` out of the deps, otherwise a
  // failing request would refetch in a loop.
  const dictionaryFailedRef = useRef(false)

  dictionaryFailedRef.current = dictionary.isError

  const refetchDictionary = dictionary.refetch

  useEffect(() => {
    if (isActive && dictionaryFailedRef.current) void refetchDictionary()
  }, [isActive, refetchDictionary])

  // Loads the wordlist and reports whether it worked. Shared by the first load and by Retry.
  const loadChecker = useCallback(async (): Promise<boolean> => {
    try {
      setChecker(await loadSpellChecker())
      setCheckerFailed(false)

      return true
    } catch (error) {
      logger.error('Spell check wordlist failed to load', error)
      setCheckerFailed(true)

      return false
    }
  }, [])

  useEffect(() => {
    if (isActive && !checker) void loadChecker()
  }, [isActive, checker, loadChecker])

  // `idle` until the user presses Retry, `running` while it works, `failed` when it failed again.
  // That is what moves the status from the first error to "unavailable", as designed.
  const [retryPhase, setRetryPhase] = useState<'idle' | 'running' | 'failed'>('idle')
  const dictionaryIsError = dictionary.isError

  const handleRetry = useCallback(async () => {
    setRetryPhase('running')

    const worked = await Promise.all([
      checker ? true : loadChecker(),
      dictionaryIsError ? refetchDictionary().then((result) => !result.isError) : true,
    ])

    setRetryPhase(worked.every(Boolean) ? 'idle' : 'failed')
  }, [checker, dictionaryIsError, loadChecker, refetchDictionary])

  // The panel is memoized and doesn't re-render as the user types, so the frames say when something
  // changed: a keystroke, the caret moving, overflow, a frame mounting or unmounting. While the
  // panel is closed nothing is subscribed, so typing costs the editor nothing extra.
  const framesRevision = useSyncExternalStore(
    isActive ? subscribeToShapeTextFrames : NEVER_NOTIFIES,
    getShapeTextFramesRevision
  )

  // Bumped after a Change writes into the text, so the next scan reads the rewritten text even
  // when no frame reports the write.
  const [textEditRevision, bumpTextEditRevision] = useReducer((n: number) => n + 1, 0)
  // The text the scan read when a Change was written, until a scan of the rewritten text lands.
  const [textKeyBeforeWrite, setTextKeyBeforeWrite] = useState<string | null>(null)

  // Reading the live shapes is the one impure step, so it only happens when a signal says the
  // text may have changed: the frames' revision, the editor's style and chain revision, a Change,
  // or a new spread or page layout.
  const textKey = useMemo(() => {
    if (!isActive) return null

    return JSON.stringify(
      collectShapeTexts(shapeManagerRef?.current?.getShapes() ?? [], {
        spineX,
        chains: textChains,
      })
    )
    // The shapes and frames are read from live editor state, so these dependencies are change
    // signals that say when to read again, not values used in the body: the exhaustive-deps rule
    // would drop them, and then the memo would never refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isActive,
    shapeManagerRef,
    spineX,
    framesRevision,
    textPanelRevision,
    textEditRevision,
    textChains,
    spreadId,
  ])
  const texts = useMemo(
    () => (textKey === null ? null : (JSON.parse(textKey) as SpellText[])),
    [textKey]
  )
  const customWords = dictionary.data?.customWords ?? NO_WORDS
  const roster = dictionary.data?.roster

  // The scanner remembers each paragraph it has checked, so a rescan after an edit only checks what
  // changed. It is rebuilt whenever something that changes the answer for the same text does.
  const scanner = useMemo(
    () =>
      checker
        ? createSpellScanner(checker, {
            customWords,
            rosterWords: rosterWords(roster),
            useCustomWords: settings.dictionaries.custom,
            useRoster: settings.dictionaries.studentList,
            caseSensitive,
          })
        : null,
    [checker, customWords, roster, settings.dictionaries, caseSensitive]
  )

  const scanned = useMemo(() => {
    if (texts === null || !scanner || !dictionary.data) return null

    return buildSpellIssues(texts, scanner, caseSensitive)
  }, [texts, scanner, dictionary.data, caseSensitive])

  const getSuggestions = useMemo(
    () => (checker ? createSuggester(checker) : NO_SUGGESTIONS),
    [checker]
  )

  // A word added from the fix popover stays resolved only while it is in the dictionary, so taking
  // it out (here, in Edit Dictionary, or by another editor) flags it again (WMPCM-13377).
  const state = useMemo<SpellCheckState>(
    () => ({
      issues: scanned
        ? applyResolutions(scanned, reopenRemovedWords(resolved, customWords, caseSensitive))
        : [],
      customWords,
      ...settings,
      caseSensitive,
    }),
    [scanned, resolved, customWords, settings, caseSensitive]
  )

  // The words to underline on the canvas: the open misspellings, only while the panel is open and
  // Show underlines is on.
  const underlineRanges = useMemo(
    () => (isActive && settings.showUnderlines ? underlineRangesByShape(state.issues) : null),
    [isActive, settings.showUnderlines, state.issues]
  )

  // Publishing to the canvas is syncing with an outside store, so it is an effect. A frame whose
  // words did not change keeps its ranges, so only the frames whose text changed redraw.
  useEffect(() => {
    setSpellUnderlines(underlineRanges ?? NO_UNDERLINES)
  }, [underlineRanges])

  // Closing the panel, or leaving the editor, takes every underline off the canvas.
  useEffect(() => clearSpellUnderlines, [])

  // The word the popover is open on is marked on the canvas so the popover can open under it, with
  // or without squiggles. A word in hidden text also opens with its frame selected and its hidden
  // text showing until the popover closes (SC-5). The frame is not resized and nothing is saved.
  const handleFocusWord = useCallback(
    (word: SpellFocusWord | null) => {
      const hiddenShapeId = word?.overflow ? word.shapeId : null

      setSpellActiveWord(word && { shapeId: word.shapeId, start: word.start, end: word.end })
      setSpellPeek(hiddenShapeId)

      if (hiddenShapeId) shapeManagerRef?.current?.selectShapes([hiddenShapeId])
    },
    [shapeManagerRef]
  )

  const handleStateChange = useCallback(
    (next: SpellCheckState) => {
      setSettings({
        showUnderlines: next.showUnderlines,
        dictionaries: next.dictionaries,
      })
      setCaseSensitiveBySpread((bySpread) =>
        bySpread[spreadKey] === next.caseSensitive
          ? bySpread
          : { ...bySpread, [spreadKey]: next.caseSensitive }
      )

      // Change and Change All write the replacement into the text (WMPCM-13377). An instance that
      // could not be written (its frame is locked, or its text moved since the scan) stays open.
      const changed = newlyChangedOccurrences(state.issues, next.issues)
      let { issues } = next

      if (changed.length > 0) {
        const written =
          shapeManagerRef?.current?.replaceShapeTextRanges(
            changeReplacements(changed, texts ?? [])
          ) ?? []

        issues = reopenOccurrences(
          next.issues,
          changed.filter((_, i) => !written[i])
        )

        if (written.some(Boolean)) {
          // Kept from the first write until a scan of the rewritten text lands (below).
          setTextKeyBeforeWrite((before) => before ?? textKey)
          bumpTextEditRevision()
        }
      }

      // A word ignored with Ignore All stays ignored while it is not in the text, so it is carried
      // over even though the scan has no issue for it now.
      const carried = resolved.filter(
        (previous) => previous.ignoredAll && !issues.some((issue) => issue.id === previous.id)
      )

      setResolved([
        ...issues.filter((issue) => issue.occurrences.some((o) => o.status !== 'open')),
        ...carried,
      ])
      setResolvedTexts(texts)
      syncWords(state.customWords, next.customWords)
    },
    [
      state.customWords,
      state.issues,
      resolved,
      syncWords,
      spreadKey,
      shapeManagerRef,
      texts,
      textKey,
    ]
  )

  // The user's choices stay on the instance they were made on when the text is edited: each moves
  // with the text around it and is forgotten when its word is edited or deleted, so the word typed
  // again is a new misspelling (SC-14). Edits made while the panel was closed are caught up with
  // here too, as `texts` is only read while it is open.
  let nextResolved = resolved
  let nextResolvedTexts = resolvedTexts

  if (texts !== null && resolvedTexts !== null && texts !== resolvedTexts) {
    nextResolved = trackResolutionsThroughEdits(resolved, resolvedTexts, texts)
    nextResolvedTexts = texts
  }

  // Once the scan reads the rewritten text, the written `changed` choices have done their job of
  // hiding the old instances until then. Kept any longer, they would be matched onto the same word
  // typed again and hide it (`forgetWrittenChanges`). Until then they are needed: a mounted frame
  // can report its new text a render after the write.
  if (textKeyBeforeWrite !== null && textKey !== textKeyBeforeWrite) {
    setTextKeyBeforeWrite(null)
    nextResolved = forgetWrittenChanges(nextResolved)
  }

  if (nextResolved !== resolved || nextResolvedTexts !== resolvedTexts) {
    setResolved(nextResolved)
    setResolvedTexts(nextResolvedTexts)
  }

  const isFailing = checkerFailed || dictionary.isError

  // Once things work again, forget an earlier failed retry so the next failure starts at "error".
  if (retryPhase === 'failed' && scanned && !isFailing) setRetryPhase('idle')

  let status: SpellCheckStatus = 'loading'

  if (retryPhase === 'running') status = 'retrying'
  else if (isFailing) status = retryPhase === 'failed' ? 'unavailable' : 'error'
  else if (scanned) status = 'ready'

  // A failed query keeps `isError` while a retry runs, so that retry reads as loading.
  let dictionaryStatus: DictionaryStatus = 'loading'

  if (dictionary.isError && !dictionary.isFetching) dictionaryStatus = 'error'
  else if (dictionary.data) dictionaryStatus = 'ready'

  return {
    state,
    status,
    dictionaryStatus,
    onStateChange: handleStateChange,
    addWord,
    removeWord,
    clearWords,
    getSuggestions,
    onFocusWord: handleFocusWord,
    onRetry: () => void handleRetry(),
    spreadId,
  }
}
